import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  BarChart as BarChartIcon,
  Timeline as LineChartIcon,
  PieChart as PieChartIcon,
  ScatterPlot as ScatterChartIcon
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import { Line, Bar, Pie, Scatter } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { useAuth } from '../../../../auth/useAuth';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  TimeScale
);

interface PopupChartProps {
  open: boolean;
  onClose: () => void;
  initialData?: {
    fundId?: number;
    fieldId?: number;
    chartType?: string;
    title?: string;
  };
}

interface ChartData {
  labels: string[];
  datasets: any[];
}

const PopupChart: React.FC<PopupChartProps> = ({ open, onClose, initialData }) => {
  const { getAccessToken } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Chart configuration
  const [chartType, setChartType] = useState(initialData?.chartType || 'line');
  const [selectedFund, setSelectedFund] = useState(initialData?.fundId?.toString() || '');
  const [selectedField, setSelectedField] = useState(initialData?.fieldId?.toString() || '');
  const [dateRange, setDateRange] = useState('6M');
  const [showThresholds, setShowThresholds] = useState(true);
  
  // Data
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [funds, setFunds] = useState<any[]>([]);
  const [fields, setFields] = useState<any[]>([]);
  const [thresholds, setThresholds] = useState<any[]>([]);
  
  // Chart types
  const chartTypes = [
    { value: 'line', label: 'Line Chart', icon: <LineChartIcon /> },
    { value: 'bar', label: 'Bar Chart', icon: <BarChartIcon /> },
    { value: 'pie', label: 'Pie Chart', icon: <PieChartIcon /> },
    { value: 'scatter', label: 'Scatter Plot', icon: <ScatterChartIcon /> }
  ];

  // Load initial data
  useEffect(() => {
    if (open) {
      loadFunds();
      loadFields();
    }
  }, [open]);

  // Load chart data when parameters change
  useEffect(() => {
    if (open && selectedFund && selectedField) {
      loadChartData();
    }
  }, [open, selectedFund, selectedField, dateRange, chartType]);

  const loadFunds = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/funds`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFunds(data);
      }
    } catch (err) {
      console.error('Error loading funds:', err);
    }
  };

  const loadFields = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-fields`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFields(data);
      }
    } catch (err) {
      console.error('Error loading fields:', err);
    }
  };

  const loadChartData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token available');

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      
      switch (dateRange) {
        case '1M':
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case '3M':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case '6M':
          startDate.setMonth(endDate.getMonth() - 6);
          break;
        case '1Y':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        case '2Y':
          startDate.setFullYear(endDate.getFullYear() - 2);
          break;
      }

      // Load field history data using the API endpoint
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/field-history/${selectedFund}/${selectedField}?start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate.toISOString().split('T')[0]}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error('Failed to load chart data');

      const result = await response.json();
      const fieldHistory = result.data || [];

      // Load thresholds if enabled
      let thresholdData = [];
      if (showThresholds) {
        const thresholdResponse = await fetch(
          `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-thresholds?backstop_id=${selectedFund}&field_id=${selectedField}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (thresholdResponse.ok) {
          thresholdData = await thresholdResponse.json();
          setThresholds(thresholdData);
        }
      }

      // Process data for chart
      const processedData = processChartData(fieldHistory, thresholdData);
      setChartData(processedData);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chart data');
    } finally {
      setLoading(false);
    }
  };

  const processChartData = (fieldHistory: any[], thresholdData: any[]): ChartData => {
    if (!fieldHistory.length) {
      return { labels: [], datasets: [] };
    }

    // Sort by date
    const sortedData = fieldHistory.sort((a, b) => new Date(a.MonitorDate).getTime() - new Date(b.MonitorDate).getTime());
    
    const labels = sortedData.map(item => new Date(item.MonitorDate).toLocaleDateString());
    const values = sortedData.map(item => item.FieldValue);

    const datasets: any[] = [];

    // Main data series
    if (chartType === 'line') {
      datasets.push({
        label: fields.find(f => f.FieldID.toString() === selectedField)?.FieldName || 'Field Value',
        data: values,
        borderColor: '#1a237e',
        backgroundColor: '#1a237e20',
        fill: false,
        tension: 0.1
      });
    } else if (chartType === 'bar') {
      datasets.push({
        label: fields.find(f => f.FieldID.toString() === selectedField)?.FieldName || 'Field Value',
        data: values,
        backgroundColor: '#1a237e',
        borderColor: '#1a237e',
        borderWidth: 1
      });
    } else if (chartType === 'pie') {
      // For pie chart, group by value ranges
      const ranges = groupValuesByRanges(values);
      return {
        labels: Object.keys(ranges),
        datasets: [{
          data: Object.values(ranges),
          backgroundColor: [
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#4BC0C0',
            '#9966FF',
            '#FF9F40'
          ]
        }]
      };
    } else if (chartType === 'scatter') {
      datasets.push({
        label: fields.find(f => f.FieldID.toString() === selectedField)?.FieldName || 'Field Value',
        data: sortedData.map((item, index) => ({ x: index, y: item.FieldValue })),
        backgroundColor: '#1a237e',
        borderColor: '#1a237e'
      });
    }

    // Add threshold lines for line and bar charts
    if (showThresholds && thresholdData.length > 0 && (chartType === 'line' || chartType === 'bar')) {
      const threshold = thresholdData[0];
      
      if (threshold.SoftUpper) {
        datasets.push({
          label: 'Soft Upper Threshold',
          data: new Array(labels.length).fill(threshold.SoftUpper),
          borderColor: '#ff9800',
          backgroundColor: 'transparent',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        });
      }
      
      if (threshold.HardUpper) {
        datasets.push({
          label: 'Hard Upper Threshold',
          data: new Array(labels.length).fill(threshold.HardUpper),
          borderColor: '#f44336',
          backgroundColor: 'transparent',
          borderDash: [10, 5],
          pointRadius: 0,
          fill: false
        });
      }
      
      if (threshold.SoftLower) {
        datasets.push({
          label: 'Soft Lower Threshold',
          data: new Array(labels.length).fill(threshold.SoftLower),
          borderColor: '#ff9800',
          backgroundColor: 'transparent',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        });
      }
      
      if (threshold.HardLower) {
        datasets.push({
          label: 'Hard Lower Threshold',
          data: new Array(labels.length).fill(threshold.HardLower),
          borderColor: '#f44336',
          backgroundColor: 'transparent',
          borderDash: [10, 5],
          pointRadius: 0,
          fill: false
        });
      }
    }

    return { labels, datasets };
  };

  const groupValuesByRanges = (values: number[]) => {
    const ranges: Record<string, number> = {};
    
    values.forEach(value => {
      if (value < 0.01) ranges['< 1%'] = (ranges['< 1%'] || 0) + 1;
      else if (value < 0.05) ranges['1% - 5%'] = (ranges['1% - 5%'] || 0) + 1;
      else if (value < 0.1) ranges['5% - 10%'] = (ranges['5% - 10%'] || 0) + 1;
      else if (value < 0.2) ranges['10% - 20%'] = (ranges['10% - 20%'] || 0) + 1;
      else ranges['> 20%'] = (ranges['> 20%'] || 0) + 1;
    });
    
    return ranges;
  };

  const exportChart = () => {
    if (!chartData) return;
    
    // Create CSV export
    const csvContent = [
      ['Date', 'Value'],
      ...chartData.labels.map((label, index) => [
        label,
        chartData.datasets[0]?.data[index] || ''
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `chart_data_${selectedFund}_${selectedField}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: `${funds.find(f => f.BackstopID.toString() === selectedFund)?.FundName || 'Fund'} - ${fields.find(f => f.FieldID.toString() === selectedField)?.FieldName || 'Field'}`
      }
    },
    scales: chartType !== 'pie' ? {
      y: {
        beginAtZero: false
      }
    } : undefined
  };

  const renderChart = () => {
    if (!chartData) return null;

    switch (chartType) {
      case 'line':
        return <Line data={chartData} options={chartOptions} />;
      case 'bar':
        return <Bar data={chartData} options={chartOptions} />;
      case 'pie':
        return <Pie data={chartData} options={chartOptions} />;
      case 'scatter':
        return <Scatter data={chartData} options={chartOptions} />;
      default:
        return <Line data={chartData} options={chartOptions} />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Interactive Chart Generation
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh Data">
              <IconButton onClick={loadChartData} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export Chart Data">
              <IconButton onClick={exportChart} disabled={!chartData}>
                <ExportIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Chart Configuration */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Chart Configuration
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Chart Type</InputLabel>
                  <Select
                    value={chartType}
                    onChange={(e) => setChartType(e.target.value)}
                    label="Chart Type"
                  >
                    {chartTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {type.icon}
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Fund</InputLabel>
                  <Select
                    value={selectedFund}
                    onChange={(e) => setSelectedFund(e.target.value)}
                    label="Fund"
                  >
                    {funds.map(fund => (
                      <MenuItem key={fund.BackstopID} value={fund.BackstopID.toString()}>
                        {fund.FundName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Field</InputLabel>
                  <Select
                    value={selectedField}
                    onChange={(e) => setSelectedField(e.target.value)}
                    label="Field"
                  >
                    {fields.map(field => (
                      <MenuItem key={field.FieldID} value={field.FieldID.toString()}>
                        {field.FieldName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    label="Date Range"
                  >
                    <MenuItem value="1M">Last Month</MenuItem>
                    <MenuItem value="3M">Last 3 Months</MenuItem>
                    <MenuItem value="6M">Last 6 Months</MenuItem>
                    <MenuItem value="1Y">Last Year</MenuItem>
                    <MenuItem value="2Y">Last 2 Years</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2">Options:</Typography>
              <Chip
                label="Show Thresholds"
                color={showThresholds ? 'primary' : 'default'}
                onClick={() => setShowThresholds(!showThresholds)}
                variant={showThresholds ? 'filled' : 'outlined'}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Chart Display */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 400 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading chart data...
                </Typography>
              </Box>
            ) : chartData ? (
              <Box sx={{ height: 400 }}>
                {renderChart()}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
                <Typography variant="body2" color="text.secondary">
                  Select fund and field to generate chart
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PopupChart;
